import React, { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';

interface MobileStickyAdProps {
  className?: string;
}

/**
 * Simple mobile sticky banner ad (320x50 or 320x100)
 * Standard implementation following Google AdSense best practices
 */
export const MobileStickyAd: React.FC<MobileStickyAdProps> = ({ className = '' }) => {
  const [isClosed, setIsClosed] = useState(false);
  const adRef = useRef<HTMLDivElement>(null);
  const initializedRef = useRef(false);

  useEffect(() => {
    // Check if user previously closed the ad
    const closed = localStorage.getItem('mobileAdClosed');
    if (closed === 'true') {
      setIsClosed(true);
    }
  }, []);

  useEffect(() => {
    // Only initialize once when component mounts
    if (!initializedRef.current && adRef.current && !isClosed) {
      try {
        // Push ad to AdSense queue
        (window.adsbygoogle = window.adsbygoogle || []).push({});
        initializedRef.current = true;
      } catch (err) {
        console.error('AdSense initialization error:', err);
      }
    }
  }, [isClosed]);

  const handleClose = () => {
    setIsClosed(true);
    try {
      localStorage.setItem('mobileAdClosed', 'true');
    } catch (e) {
      // Ignore localStorage errors
    }
  };

  if (isClosed) {
    return null;
  }

  return (
    <div className={`fixed bottom-0 left-0 right-0 z-50 ${className}`}>
      <div className="relative bg-white border-t border-gray-200 shadow-lg">
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-1 right-1 z-10 w-6 h-6 bg-gray-700 hover:bg-gray-800 text-white rounded-full flex items-center justify-center"
          aria-label="Close ad"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Ad container */}
        <div 
          ref={adRef}
          className="flex justify-center items-center p-2"
          style={{ minHeight: '60px' }}
        >
          <ins
            className="adsbygoogle"
            style={{ display: 'inline-block', width: '320px', height: '50px' }}
            data-ad-client="ca-pub-1421631996968165"
            data-ad-slot="6995821283"
          />
        </div>
      </div>
    </div>
  );
};

export default MobileStickyAd;
